/**
 * The only place in the app that talks to the network.
 *
 * Everything goes through `request()`, so authentication, error handling and the
 * base URL are defined once. Pages and hooks call the small helpers below.
 */

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "/api";

/**
 * Thrown for any non-2xx answer. `status` lets callers react to specific cases —
 * most importantly 401, which the API uses to say "sign in to see more rows".
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** The API asks for sign-in when you filter or page while signed out. */
  get needsSignIn(): boolean {
    return this.status === 401;
  }

  /** Signed in, but not an admin. */
  get isForbidden(): boolean {
    return this.status === 403;
  }
}

export type QueryParams = Record<string, string | number | undefined | null>;

type RequestOptions = {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  params?: QueryParams;
  body?: unknown;
  /** Clerk session token. Required for admin writes and for deep/filtered reads. */
  token?: string | null;
  signal?: AbortSignal;
};

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", params, body, token, signal } = options;

  const url = new URL(`${BASE_URL}${path}`, window.location.origin);
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value !== undefined && value !== null && value !== "") {
      url.searchParams.set(key, String(value));
    }
  }

  const headers: Record<string, string> = {};
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (token) headers.Authorization = `Bearer ${token}`;

  const response = await fetch(url.toString(), {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    throw new ApiError(response.status, await readErrorMessage(response));
  }

  // DELETE sometimes answers with an empty body.
  const text = await response.text();
  return (text ? JSON.parse(text) : {}) as T;
}

async function readErrorMessage(response: Response): Promise<string> {
  try {
    const parsed = (await response.json()) as { error?: string };
    if (parsed.error) return parsed.error;
  } catch {
    // Not JSON — fall through to a generic message.
  }
  return `Request failed (${response.status})`;
}

export function apiGet<T>(path: string, options?: Omit<RequestOptions, "method" | "body">) {
  return request<T>(path, { ...options, method: "GET" });
}

export function apiPost<T>(path: string, body: unknown, token: string | null) {
  return request<T>(path, { method: "POST", body, token });
}

export function apiPatch<T>(path: string, body: unknown, token: string | null) {
  return request<T>(path, { method: "PATCH", body, token });
}

export function apiDelete<T>(path: string, token: string | null) {
  return request<T>(path, { method: "DELETE", token });
}
